/* eslint-disable @typescript-eslint/no-explicit-any */
import { useGlobal } from '@@/contexts/GlobalProvider';
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@@/ui/primitives/dialog';
import { Input } from '@@/ui/primitives/input';
import { Button } from '@@/ui/primitives/button';
import { cn, formatUserName } from '@@/lib/utils';
import { Spinner } from '@@/ui/components/Spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@@/ui/primitives/select';
import { Textarea } from '@@/ui/primitives/textarea';
import { useForm } from 'react-hook-form';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@@/ui/primitives/form';
import type { CreateCaseInput } from '@@/products/cases/types';
import { AttachmentUploader } from '@@/products/cases/components/AttachmentUploader';
import { Label } from '@@/ui/primitives/label';
import { useGetUserRolePermissions } from '@@/hooks/useGetUserRolePermissions';
import { EarphoneCaseIcon } from '@@/icons';
import { useCreateCase } from '@@/products/cases/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { CaseStatus } from '@@/products/cases/types/api.types';
import { CASE_TYPE_OPTIONS, PRIORITY_OPTIONS } from '@@/products/cases/constant/caseConstants';
import { fileToBase64 } from '@@/products/cases/helpers/ui-helper';

type FormValues = {
  template: string;
  title: string;
  customerEmail?: string;
  status: CaseStatus;
  summary?: string;
  caseType: 'Bug Report' | 'Feature Request' | 'Technical Support' | 'Account Issue' | 'Other';
  priority: 'Low' | 'Medium' | 'High' | 'Critical' | 'Urgent';
  name: string;
  companyName: string;
  attachments?: File[];
};

const getDefaultFormValues = (userEmail: string, userName: string, companyName: string): FormValues => ({
  template: '',
  customerEmail: userEmail,
  name: userName,
  companyName: companyName,
  title: '',
  priority: 'Low',
  status: 'open',
  summary: '',
  caseType: 'Bug Report'
});

const OpenCaseModal = () => {
  const { isCasesModalOpen, cases, colorScheme } = useGlobal();
  const { user: userAttributes } = useGetUserRolePermissions();
  const queryClient = useQueryClient();

  // Derive user info from userAttributes
  const userInfo = useMemo(
    () => ({
      email: userAttributes?.email || '',
      name: userAttributes?.email ? formatUserName(userAttributes.email.split('@')[0]) : '',
      companyName: userAttributes?.companyName || ''
    }),
    [userAttributes]
  );

  const primaryColor = colorScheme === 'cxcentral' ? '#0EA2A7' : '#6488E9';
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);

  const createCaseMutation = useCreateCase({
    onSuccess: () => {
      cases.closeModal();
      queryClient.invalidateQueries({
        queryKey: ['cases'],
        refetchType: 'active'
      });
    }
  });

  const form = useForm<FormValues>({
    defaultValues: getDefaultFormValues(userInfo.email, userInfo.name, userInfo.companyName)
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isCasesModalOpen) {
      form.reset(getDefaultFormValues(userInfo.email, userInfo.name, userInfo.companyName));
      setAttachmentFiles([]);
    }
  }, [isCasesModalOpen, userInfo, form]);

  const handleSubmit = async (data: FormValues) => {
    const createPayload: CreateCaseInput = {
      title: data.title,
      status: data.status.toLowerCase(),
      customerEmail: data.customerEmail as string,
      summary: data.summary,
      priority: data.priority,
      caseType: data.caseType,
      companyName: data.companyName as string
    };

    // Convert attachments to base64 if present
    if (attachmentFiles.length > 0) {
      const attachmentsPromises = attachmentFiles.map(async (file) => {
        const base64Content = await fileToBase64(file);
        return {
          filename: file.name,
          content: base64Content,
          content_type: file.type
        };
      });

      const attachments = await Promise.all(attachmentsPromises);
      createPayload.attachments = attachments;
    }

    createCaseMutation.mutate(createPayload);
  };

  const handleClose = () => {
    cases.closeModal();
  };

  return (
    <Dialog open={isCasesModalOpen} onOpenChange={(open) => (open ? cases.openModal() : cases.closeModal())}>
      <DialogContent
        className='sm:max-w-[900px] w-full max-h-[80vh] overflow-y-auto'
        style={{ ['--primary-color' as any]: primaryColor }}
      >
        <DialogHeader className='flex justify-center items-center gap-0 mb-6'>
          <DialogTitle>
            <EarphoneCaseIcon />
          </DialogTitle>
          <DialogDescription className='font-mona-expanded text-3xl font-semibold text-black'>
            Open a Case
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className='bg-[rgba(249,249,249,0.63)] border border-[#F2F2F2] px-10 py-8 rounded-xl max-w-[800px] mx-auto w-full space-y-6'
          >
            <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
              {/* <FormField
                  control={form.control}
                  rules={{ required: 'Template is required' }}
                  name='template'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Template</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger className='!h-10 w-full'>
                            <SelectValue placeholder='Choose a template' />
                          </SelectTrigger>
                          <SelectContent>
                            {TEMPLATES.map((template) => (
                              <SelectItem key={template.id} value={template.name}>
                                <div className='flex items-center gap-2'>
                                  <span>{template.name}</span>
                                  <Badge variant='outline' className='text-xs'>
                                    {template.fields?.filter((f) => !f.system).length || 0} fields
                                  </Badge>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}
              <FormField
                control={form.control}
                name='name'
                rules={{ required: 'Name is required' }}
                render={({ field }) => (
                  <FormItem className=''>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} className='h-10' disabled />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='customerEmail'
                render={({ field }) => (
                  <FormItem className=''>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value} className='h-10' disabled />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='companyName'
                rules={{ required: 'Company ID is required' }}
                render={({ field }) => (
                  <FormItem className='md:col-span-2'>
                    <FormLabel>Company</FormLabel>
                    <FormControl>
                      <Input {...field} className='h-10' disabled />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='title'
                rules={{ required: 'Title is required' }}
                render={({ field }) => (
                  <FormItem className='md:col-span-2'>
                    <FormLabel>Case Title</FormLabel>
                    <FormControl>
                      <Input {...field} className='h-10' />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='caseType'
                rules={{ required: 'Case Type is required' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Case Type</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className='!h-10 w-full'>
                          <SelectValue placeholder='Case Type' />
                        </SelectTrigger>
                        <SelectContent>
                          {CASE_TYPE_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='priority'
                rules={{ required: 'Priority is required' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className='!h-10 w-full'>
                          <SelectValue placeholder='Priority' />
                        </SelectTrigger>
                        <SelectContent>
                          {PRIORITY_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='summary'
                rules={{ required: 'Summary is required' }}
                render={({ field }) => (
                  <FormItem className='md:col-span-2'>
                    <FormLabel>Summary</FormLabel>
                    <FormControl>
                      <Textarea {...field} className='min-h-48' />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div>
              <Label className='mb-2'>Attachments</Label>
              <AttachmentUploader files={attachmentFiles} onFilesChange={setAttachmentFiles} variant='compact' />
            </div>

            <DialogFooter className='flex flex-col sm:flex-row gap-3 max-w-[800px] mx-auto w-full mt-8'>
              <Button type='button' variant='outline' className='rounded-lg' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type='submit'
                className={cn(
                  'rounded-lg text-white',
                  'bg-[var(--primary-color)] hover:bg-[var(--primary-color)]/80',
                  createCaseMutation.isPending && 'opacity-50 cursor-not-allowed'
                )}
                disabled={createCaseMutation.isPending}
              >
                {createCaseMutation.isPending ? (
                  <>
                    <Spinner className='w-4 h-4 mr-2' /> Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default OpenCaseModal;
